import { deriveAnchoredAge } from '../utils/levelCalculations';
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Scatter, ZAxis
} from 'recharts';
import { FaChartBar, FaEdit, FaUser, FaList, FaTree } from 'react-icons/fa';
import { Rectangle } from 'recharts';
import { DIMENSION_STYLES, UnifiedHeader, UnifiedLegend } from '../styles/DimensionStyles';
import { calculateExpectedPercentage, calculatePreviousYearPercentage } from '../utils/levelCalculations';
import { calculateFormProgress } from '../utils/gradeCalculationForWithdrawal';

// Definimos las interfaces localmente
interface Skill {
  id: number;
  name: string;
  levels: string[];
  selectedLevelIndex: number | null;
}

interface SubDimension {
  id: number;
  name: string;
  skills: Skill[];
}

interface Dimension {
  id: number;
  name: string;
  skills: Skill[]; // Para compatibilidad
  subDimensions?: SubDimension[];
}

interface Student {
  id: number | string;
  name: string;
  course: string;
  birthDate: string;
  skillLevels: SkillLevel[];
  previousYearLevels?: {
    dimensionId: number;
    skillId: number;
    level: number | null;
    year: string;
  }[];
  qualitativeDescription?: string;
}

interface SkillLevel {
  dimensionId: number;
  skillId: number;
  level: number | null;
  notEvaluated?: boolean;
}

interface DimensionSummaryProps {
  student: Student;
  dimensionId: number;
}

const COLORS = {
  previousYear: '#6B7280',
  achieved: '#4A90E2',
  expected: '#F5A623'
};

const CustomBar = (props: any) => {
  const { x, y, width, height, fill } = props;
  return <Rectangle x={x} y={y} width={width} height={height} fill={fill} />;
};

const CustomVerticalRectangle = (props: any) => {
  const { cx, cy, fill } = props;
  return (
    <Rectangle
      x={cx - 1}
      y={cy - 25}
      width={4}
      height={40}
      fill={fill}
    />
  );
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-2 border rounded shadow">
        <p className="label">{`${label}`}</p>
        {payload.map((entry: any, index: number) => (
          <p key={`item-${entry.name}-${index}`} style={{ color: entry.fill }}>
            {`${entry.name}: ${entry.value ? entry.value.toFixed(2) : 'N/A'}%`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Habilidades de Encuentro con la naturaleza (subdimensión 1)
const encuentroNaturalezaSkills: Skill[] = [
  {
    "id": 1,
        "name": "Observa la naturaleza, un objeto o evento a través de los sentidos, para obtener información",
    "levels": [
          "Observa la naturaleza, un objeto o evento a través de los sentidos, para obtener información",
          "No Aplica",
          "No Aplica",
          "No Aplica",
          "Observa y luego describe los eventos o fenómenos apoyándose en la información obtenida.",
          "Formula inferencias y predicciones, en forma guiada, sobre lo que observa y le llama la atención del entorno.",
          "Formula inferencias y predicciones, de forma autónoma, sobre lo que observa y le llama la atención del entorno.",
          "Formula predicciones sobre las investigaciones científicas que realiza, siendo capaz de fundamentarlas en forma guiada.",
          "Formula predicciones sobre las investigaciones científicas que realiza, siendo capaz de fundamentarlas en forma autónoma."
    ],
    "selectedLevelIndex": null
},
{
    "id": 2,
        "name": "Formula preguntas simples en forma guiada, sobre lo que observa y le llama la atención",
    "levels": [
          "Formula preguntas simples en forma guiada, sobre lo que observa y le llama la atención",
          "Formula preguntas simples en forma guiada, para clarificar hechos sobre lo que observa y le llama la atención.",
          "Formula preguntas simples para clarificar hechos, sobre lo que observa y le llama la atención.",
          "Formula preguntas simples para clarificar hechos y centrarse en la información importante, sobre lo que observa y le llama la atención.",
          "Formula preguntas simples para clarificar hechos y centrarse en la información importante, sobre lo que observa y le llama la atención, generando nueva información.",
          "No Aplica",
          "Formula preguntas para clarificar hechos y centrarse en la información importante, sobre lo que observa y le llama la atención, generando nueva información.",
          "Formula preguntas significativas que le permitan investigar el entorno que lo rodea, de forma autónoma.",
          "Formula preguntas simples de carácter científico, que le permiten investigar el entorno que le rodea"
    ],
    "selectedLevelIndex": null
},
{
    "id": 3,
        "name": "Explora el entorno natural a través de los sentidos, manifestando asombro e interés",
    "levels": [
          "Explora el entorno natural a través de los sentidos, manifestando asombro e interés",
          "No Aplica",
          "Explora la naturaleza a través de los sentidos, cuidándola con ayuda de un adulto.",
          "No Aplica",
          "Explora la naturaleza a través de los sentidos, cuidándola de manera autónoma.",
          "No Aplica",
          "Explora la naturaleza a través de los sentidos, cuidándola de manera autónoma, explicando el rol que cumple el ser humano en su cuidado.",
          "No Aplica",
          "No Aplica"
    ],
    "selectedLevelIndex": null
},
{
    "id": 4,
        "name": "Experimenta en forma espontánea, con elementos del entorno y manipulando materiales simples",
    "levels": [
          "Experimenta en forma espontánea, con elementos del entorno y manipulando materiales simples",
          "No Aplica",
          "Experimenta en forma guiada, con elementos del entorno y manipulando materiales simples.",
          "Experimenta y explora, en forma guiada, manipulando materiales simples y la medición con unidades no estandarizadas.",
          "Experimenta y explora, a partir de preguntas dadas, manipulando y clasificando materiales simples en forma individual y colaborativa.",
          "No Aplica",
          "Participan en diferentes tipos de investigaciones (experimentales y no experimentales), respondiendo preguntas dadas por medio de la manipulación y clasificación de la evidencia.",
          "No Aplica",
          "No Aplica"
    ],
    "selectedLevelIndex": null
},
{
    "id": 5,
        "name": "Investiga utilizando diversas fuentes y procedimientos para conocer su entorno natural",
    "levels": [
          "No Aplica",
          "No Aplica",
          "Investiga utilizando diversas fuentes y procedimientos para conocer su entorno natural",
          "Investiga utilizando diversas fuentes y procedimientos para responder a sus inquietudes frente a su entorno natural",
          "Investiga utilizando diversas fuentes y procedimientos, buscando nuevos conocimientos para conocer el entorno natural y físico",
          "Investiga utilizando diversas fuentes y procedimientos, para responder preguntas dadas que le permitan comprender el entorno.",
          "Investiga utilizando diversas fuentes y procedimientos, para responder preguntas formuladas por ellos u otros, que le permitan comprender el entorno.",
          "No Aplica",
          "Investiga utilizando diversas fuentes y procedimientos, para responder preguntas formuladas por ellos u otros, sobre un tema de estudio, aplicando estrategias para organizar y comunicar la información."
    ],
    "selectedLevelIndex": null
},
{
    "id": 6,
        "name": "Planifica actividades o acciones individuales y colectivas, para la realización de experimentaciones simples en la naturaleza",
    "levels": [
          "No Aplica",
          "No Aplica",
          "No Aplica",
          "No Aplica",
          "Planifica actividades o acciones individuales y colectivas, para la realización de experimentaciones simples en la naturaleza.",
          "No Aplica",
          "Planifica investigaciones o establece procedimientos previos simples que le permitan llevar a cabo sus investigaciones, para responder preguntas del entorno.",
          "Planifica investigaciones o establece procedimientos previos considerando el cambio de una sola variable, para responder preguntas de un tema de estudio.",
          "Planifica investigaciones o establece procedimientos previos identificando variables que se mantienen, que cambian o que dan resultado en una investigación experimental"
    ],
    "selectedLevelIndex": null
},
{
    "id": 7,
        "name": "Usa, de forma guiada, instrumentos y herramientas de observación y recolección (lupas, frascos, embudos, pinzas, etc), para realizar sus experimentaciones espontáneas",
    "levels": [
          "Usa, de forma guiada, instrumentos y herramientas de observación y recolección (lupas, frascos, embudos, pinzas, etc), para realizar sus experimentaciones espontáneas.",
          "Usa instrumentos y herramientas de observación y recolección (lupas, frascos, embudos, pinzas, etc), para realizar sus experimentaciones guiadas por un adulto.",
          "Sigue instrucciones para usar instrumentos conociendo sus funciones, limitaciones y peligros, con ayuda de un adulto.",
          "Usa instrumentos de forma autónoma conociendo sus funciones, limitaciones y peligros.",
          "Usa instrumentos conociendo y exponiendo a los demás su uso correcto, sus funciones, limitaciones y peligros.",
          "Usa materiales e instrumentos, como vasos graduados, en forma segura y autónoma para hacer observaciones y mediciones.",
          "Usa materiales e instrumentos, como reglas y termómetros, en forma segura y autónoma para hacer observaciones y mediciones.",
          "No Aplica",
          "Selecciona los materiales e instrumentos necesarios para hacer observaciones y mediciones, usándolos en forma segura y autónoma."
    ],
    "selectedLevelIndex": null
},
{
    "id": 8,
        "name": "Mide y registra los datos observados en sus investigaciones utilizando medidas no estandarizadas",
    "levels": [
          "No Aplica",
          "No Aplica",
          "No Aplica",
          "No Aplica",
          "Mide y registra los datos observados en sus investigaciones utilizando medidas no estandarizadas.",
          "Mide y registra los datos observados en sus investigaciones en forma precisa (usando gráficos o tablas), utilizando medidas estandarizadas.",
          "Mide, registra y compara los datos observados en sus investigaciones en forma precisa (usando gráficos o tablas), utilizando medidas estandarizadas.",
          "Mide, registra y compara los datos observados en sus investigaciones en forma precisa (usando gráficos o tablas), especificando las unidades de medida.",
          "Mide, registra y compara los datos observados en sus investigaciones en forma precisa (usando gráficos o tablas), especificando las unidades de medida e identificando patrones simples."
    ],
    "selectedLevelIndex": null
},
{
    "id": 9,
        "name": "Compara estableciendo similitudes y diferencias de los cambios que observa en su entorno",
    "levels": [
          "No Aplica",
          "No Aplica",
          "Compara estableciendo similitudes y diferencias de los cambios que observa en su entorno.",
          "Compara estableciendo similitudes y diferencias sobre los dibujos y resultados que obtuvieron entre pares.",
          "Compara estableciendo similitudes y diferencias sobre la información científica y resultados que obtuvieron entre pares.",
          "No Aplica",
          "No Aplica",
          "No Aplica",
          "No Aplica"
    ],
    "selectedLevelIndex": null
},
{
    "id": 10,
        "name": "Resume las evidencias obtenidas en sus observaciones para responder a una pregunta inicial planteada",
    "levels": [
          "No Aplica",
          "No Aplica",
          "No Aplica",
          "No Aplica",
          "No Aplica",
          "Resume las evidencias obtenidas en sus observaciones para responder a una pregunta inicial planteada.",
          "Compara sus predicciones con la pregunta inicial planteada, utilizando sus observaciones como evidencia para apoyar sus ideas.",
          "No Aplica",
          "Formula explicaciones razonables y saca conclusiones mediante la comparación de sus predicciones y los resultados obtenidos en sus investigaciones."
    ],
    "selectedLevelIndex": null
},
{
    "id": 11,
        "name": "Comunica de forma oral sus ideas sobre las exploraciones que realiza de su entorno natural",
    "levels": [
          "Comunica de forma oral sus ideas sobre las exploraciones que realiza de su entorno natural.",
          "Comunica de forma oral sus ideas, observaciones y experiencias sobre las exploraciones que realiza de su entorno natural.",
          "Comunica de diferentes maneras (oral, dibujos, escrita, presentaciones, etc.), con ayuda de un adulto, sus ideas, observaciones y experiencias sobre las exploraciones que realiza de su entorno natural.",
          "Comunica de diferentes maneras (oral, dibujos, escrita, presentaciones, etc.) sus ideas, observaciones y experiencias sobre las exploraciones que realiza de su entorno natural.",
          "Comunica de diferentes maneras (oral, dibujos, escrita, presentaciones, etc.) sus ideas, observaciones, experiencias y mediciones sobre las exploraciones que realiza de su entorno natural.",
          "Comunica y compara sus ideas, observaciones, experiencias y mediciones sobre las investigaciones que realiza, mediante el uso de diagramas, modelos físicos, presentaciones o informes sencillos.",
          "Comunica y explica sus ideas, observaciones, experiencias y mediciones sobre las investigaciones que realiza, mediante el uso de diagramas, modelos físicos, informes o presentaciones usando TIC´s.",
          "Comunica evidencias y conclusiones de sus investigaciones, mediante el uso de modelos físicos, informes o presentaciones usando TIC´s.",
          "Comunica y representa evidencias y conclusiones de sus investigaciones, mediante el uso de modelos físicos, informes o presentaciones usando TIC´s."
    ],
    "selectedLevelIndex": null
},
{
    "id": 12,
        "name": "Analiza situaciones del entorno natural, formulando conjeturas a partir de los cambios observados",
    "levels": [
          "Analiza situaciones del entorno natural, formulando conjeturas a partir de los cambios observados.",
          "Analiza situaciones del entorno natural, formulando conjeturas y predicciones de posibles causas, a partir de los cambios observados.",
          "Analiza situaciones del entorno natural, formulando conjeturas y predicciones de posibles causas y/o consecuencias, a partir de los cambios observados.",
          "Analiza situaciones del entorno natural, formulando predicciones o inferencias simples de posibles causas y/o consecuencias, a partir de los cambios observados, con apoyo de un adulto.",
          "Analiza situaciones del entorno natural, formulando predicciones o inferencias simples de posibles causas y/o consecuencias, a partir de los cambios observados.",
          "No Aplica",
          "No Aplica",
          "Reflexiona sobre sus investigaciones, reconociendo fortalezas y debilidades en su planificación y procedimientos.",
          "Reflexiona y propone mejoras sobre sus investigaciones, identificando errores o aspectos a mejorar en sus planificaciones procedimientos."
        ],
        "selectedLevelIndex": null,
      }
];

// Habilidades de Sociedad (subdimensión 2)
// Los IDs empiezan desde 13 para no superponerse con Encuentro con la naturaleza (que va hasta 12)
const sociedadSkills: Skill[] = [
  {
    "id": 13,
    "name": "Señala personas cercanas a su vida como papá, mamá, hermanos, entre otros posibles.",
    "levels": [
      "Señala personas cercanas a su vida como papá, mamá, hermanos, entre otros posibles.",
      "Reconoce (indica o nombra) el rol que tiene cada integraante de su familia, tales como: padre, madre, abuela, abuelo, hermana, hermano, hija, hijo, nieta, nieto, entre otros posibles.",
      "Describe semejanzas y diferencias entre los principales roles que tiene cada integrante de su familia, tales como: padre, madre, abuela, abuelo, hermana, hermano, hija, hijo, nieta, nieto, entre otros.",
      "Reflexiona sobre los distintos roles y tareas de los miembros de su familia (mamá, abuelo, tío, hermano menor, etc.).",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
},
{
    "id": 14,
    "name": "Describe actividades habituales que se realizan en su comunidad como ir de compras, jugar en un parque, etc., reconociendo su participación.",
    "levels": [
      "Describe actividades habituales que se realizan en su comunidad como ir de compras, jugar en un parque, etc., reconociendo su participación.",
      "Describe las actividades que realiza cada miembro de su familia y su aporte para el biniestar familiar (apoyo, cuidado, proveer, alimentar, entre otros) Señalando el rol de cada uno.",
      "Describe semejanzas y diferencias entre diferentes trabajos y actividades de personas significativas de su familia y comunidad, y sus aportes para el bienestar común.",
      "Reconoce semejanzas y diferencias entre las características, los gustos y los intereses de diferentes personas.",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
},
{
    "id": 15,
    "name": "Describe el trabajo y el aporte que realizan diferentes personas de su comunidad.",
    "levels": [
      "No Aplica",
      "Describe el trabajo y el aporte que realizan diferentes personas de su comunidad.",
      "Explica los diferentes roles que puede cumplir una persona en la sociedad; por ejemplo: \"Soy hijo, hermano, nieto, amigo, estudiante, debo cumplir las normas en mi familia y en el colegio\", \"Es madre, doctora, amiga, esposa, etc.\"",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
},
{
    "id": 16,
    "name": "Exponen a sus pares costumbres y tradiciones de su familia.",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Exponen a sus pares costumbres y tradiciones de su familia.",
      "Describe costumbres familiares comunes en Chile y su región (celebraciones, ceremonias, creencias, tradiciones).",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
},
{
    "id": 17,
    "name": "Describe características de viviendas, paisajes y costumbres a través de juegos, relatos o fotos familiares cercanos a ellos.",
    "levels": [
      "Describe características de viviendas, paisajes y costumbres a través de juegos, relatos o fotos familiares cercanos a ellos.",
      "Describe caracterísitcas de viviendas, paisajes y constumbres como danzas, fiestas comida y artesanía de otras comunidades cercanas a su localidad mencionando lo que le llama la atención.",
      "Describe caracterísitcas de viviendas, paisajes y constumbres como danzas, fiestas comida y artesanía de otras comunidades del mundo explicando lo que le llama la atención.",
      "Describre a través de la ilustración o la escritura expresiones culturales locales o de otros países.",
      "Describe modelos de vivienda, costumbre y tradiciones de los pueblos estudiados, entre el pasado y el presente.",
      "Describe modos de vida de la Antigûedad con el propio (costumbres, trabajos, oficios, creencias, vestimaneta, características de las ciudades, entre otros).",
      "Describe civilizaciones (como mayas, aztecas, etc.) considerando ubicación geográfica, organización política, sistema de caminos organización de la sociedad, roles y oficios, formas de cultivo, construcciones, costumbres, vida cotidiana, entre otros.",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
},
{
    "id": 18,
    "name": "Ilustra y compara modelos de vivienda, costumbre y tradiciones de los pueblos estudiados, entre el pasado y el presente.",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Ilustra y compara modelos de vivienda, costumbre y tradiciones de los pueblos estudiados, entre el pasado y el presente.",
      "Compara modos de vida de la Antigûedad con el propio (costumbres, trabajos, oficios, creencias, vestimaneta, características de las ciudades, entre otros).",
      "Compara civilizaciones (como mayas, aztecas, etc.) considerando ubicación geográfica, organización política, sistema de caminos organización de la sociedad, roles y oficios, formas de cultivo, construcciones, costumbres, vida cotidiana, entre otros.",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
},
{
    "id": 19,
    "name": "Explica, a través de ejemplos concretos, cómo diferentes pueblos y culturas (como griegos y romanos en la antigüedad) han enfrentado de diferentes maneras el desafío de desarrollarse.",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Explica, a través de ejemplos concretos, cómo diferentes pueblos y culturas (como griegos y romanos en la antigüedad) han enfrentado de diferentes maneras el desafío de desarrollarse.",
      "Analiza comparando las princupales características de las civilizaciones americanas.",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
},
{
    "id": 20,
    "name": "Describe características de viviendas, paisajes, costumbres (danzas, fiestas, comida y artesanía) de otras comunidades del país, mencionando lo que llama su atención.",
    "levels": [
      "No Aplica",
      "Describe características de viviendas, paisajes, costumbres (danzas, fiestas, comida y artesanía) de otras comunidades del país, mencionando lo que llama su atención.",
      "Describe semejanzas y diferencias entre las viviendas, paisajes o las costumbres (danzas, fiestas, comida y artesanía) de su comunidad y las otras comunidades del mundo, explicando lo que llama su atención.",
      "Recrea viviendas de niños de países de otro continente.",
      "Ejemplifica  de expresiones culturales indígenas presentes en la sociedad actual.",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
},
{
    "id": 21,
    "name": "Describe semejanzas y diferencias entre viviendas, paisajes o costumbres (danzas, fiestas, comida y artesanía) de su localidad y las de otras localidades del país, mencionando lo que llama su atención.",
    "levels": [
      "No Aplica",
      "Describe semejanzas y diferencias entre viviendas, paisajes o costumbres (danzas, fiestas, comida y artesanía) de su localidad y las de otras localidades del país, mencionando lo que llama su atención.",
      "Describe diferecias y similitudes al comparar costumbres cotidiandas del pasado y del presente a través de relatos, documentación gráfica o audiovisual, explicando lo que llama su atención.",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
},
{
    "id": 22,
    "name": "Recolecta información de sus experiencias personales, por medio de fuentes orales (por ejemplo, algún miembro de su familia), fotografías y objetos materiales.",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Recolecta información de sus experiencias personales, por medio de fuentes orales (por ejemplo, algún miembro de su familia), fotografías y objetos materiales.",
      "No Aplica",
      "Investiga sobre algún tema de su interés relacionado a las civilizaciones estudiadas por medio de diferentes fuentes (libros, fuentes gráficas, etc.) (como héroes, dioses, ciudades, viviendas, vestimenta, herramientas tecnológicas, entre otros)",
      "Investiga en diversas fuentes como imágenes, libros, gráficos, textos y otras sobre algunos temas relacionados con el presente de los pueblos americanos.",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
},
{
    "id": 23,
    "name": "Comunica en forma oral o con dibujos  información recolectada  en relación a la comunidad, procesos históricos u otros.",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Comunica en forma oral o con dibujos  información recolectada  en relación a la comunidad, procesos históricos u otros.",
      "Comunica valorando el legado entregado por los pueblos estudiandos",
      "Comunica lo que ha aprendido, relacionado a las civilizaciones estudiadas.",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
},
{
    "id": 24,
    "name": "Comunica la importancia de cuidar la flora y la fauna de nuestro país.",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Comunica la importancia de cuidar la flora y la fauna de nuestro país.",
      "Relaciona vegetación y fauna con los diversos paisajes de las zonas naturales de Chile",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
},
{
    "id": 25,
    "name": "Describe paisajes de las zonas norte, centro y sur de Chile.",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Describe paisajes de las zonas norte, centro y sur de Chile.",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
},
{
    "id": 26,
    "name": "Describe, dando ejemplos de su influencia en los modos de vida, las características geográficas de las zonas habitadas por algunos pueblos originarios prehispánicos estudiados.",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Describe, dando ejemplos de su influencia en los modos de vida, las características geográficas de las zonas habitadas por algunos pueblos originarios prehispánicos estudiados.",
      "Reconoce algunos factores geofráficos que influyeron en el desarrollo de las civilizaciones estudiadas (ubicación, relieve, clima, recursos naturales, etc.)",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
},
{
    "id": 27,
    "name": "Reconoce el legado indígena y español en diversas expresiones del patrimonio cultural chileno.",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Reconoce el legado indígena y español en diversas expresiones del patrimonio cultural chileno.",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
},
{
    "id": 28,
    "name": "Compara la forma de vida de las personas en el pasado y en el presente, a partir de la creación de algunos inventos.",
    "levels": [
      "No Aplica",
      "Compara la forma de vida de las personas en el pasado y en el presente, a partir de la creación de algunos inventos.",
      "Describe las necesidades y situaciones que cree que motivaron la creación de ciertos inventos.",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
},
{
    "id": 29,
    "name": "Reconoce hechos significativos en su historia personal y familiar al observar fotografías, visitas a lugares u otros.",
    "levels": [
      "Reconoce hechos significativos en su historia personal y familiar al observar fotografías, visitas a lugares u otros.",
      "Describe algunos hechos significativos de su localidad, tales como celebraciones religiosas o populares, a partir de recursos como fotografías, videos, utensilios u objetos representativos.",
      "Describe algunos hechos significativos de su localidad y país, utilizando recursos como fotografías, videos, utensilios u objetos representativos.",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
},
{
    "id": 30,
    "name": "Representa a través de distintos lenguajes artísticos (dramatiza, dibuja, modela) algunos hechos significativos de su localidad.",
    "levels": [
      "No Aplica",
      "Representa a través de distintos lenguajes artísticos (dramatiza, dibuja, modela) algunos hechos significativos de su localidad.",
      "Representa (dramatiza, dibuja, modela) algunos hechos significativos de su localidad y país.",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
},
{
    "id": 31,
    "name": "Explica por qué se conmemoran algunos hechos significativos de su localidad y país, utilizando recursos como como fotografías, videos, utensilios u objetos representativos.",
    "levels": [
      "No Aplica",
      "No Aplica",
      "Explica por qué se conmemoran algunos hechos significativos de su localidad y país, utilizando recursos como como fotografías, videos, utensilios u objetos representativos.",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
},
{
    "id": 32,
    "name": "Nombra algunos elementos del patrimonio cultural material de su localidad (por ejemplo, iglesias de Chiloé, moáis, salitreras de Humberstone, plazas o monumentos, entre otros), explicando su importancia.",
    "levels": [
      "No Aplica",
      "Nombra algunos elementos del patrimonio cultural material de su localidad (por ejemplo, iglesias de Chiloé, moáis, salitreras de Humberstone, plazas o monumentos, entre otros), explicando su importancia.",
      "Describe costumbres y tradiciones de su comunidad local o barrio, explicando la importancia que tienen.",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
},
{
    "id": 33,
    "name": "Describe algunas acciones que se realizan en su localidad para proteger el patrimonio cultural material.",
    "levels": [
      "No Aplica",
      "Describe algunas acciones que se realizan en su localidad para proteger el patrimonio cultural material.",
      "Explica por qué es importante cuidar el patrimonio y las acciones que se pueden hacer para protegerlo.",
      "No Aplica",
      "Describe atractivos del patrimonio natural de su región.",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
},
{
    "id": 34,
    "name": "Identifica instituciones significativas de su entorno.",
    "levels": [
      "Identifica instituciones significativas de su entorno.",
      "Describe el servicio que cumplen algunas instituciones, organizaciones, lugares y obras de interés patrimonial que forman parte de su localidad.",
      "Explica cómo algunos lugares de su localidad representan espacios de encuentro de la comunidad.",
      "Ilustra la labor de distintas instituciones públicas y quienes las llevan a cabo.",
      "Representa la labor de distintos servicios de la comunidad y de quienes los llevan a cabo.",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
},
{
    "id": 35,
    "name": "Describe las actividades principales que se realizan en instituciones significativas que se encuentran en su entorno.",
    "levels": [
      "Describe las actividades principales que se realizan en instituciones significativas que se encuentran en su entorno.",
      "Explica la importancia que tienen algunas instituciones, organizaciones, lugares y obras de interés patrimonial para el bienestar de su localidad.",
      "Describe el servicio que cumplen algunas instituciones, organizaciones, lugares y obras de interés patrimonial que forman parte del país y el mundo.",
      "Describe el papel de las instituciones estudiadas en la comunidad.",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
},
{
    "id": 36,
    "name": "Describe lo que llama su atención sobre el aporte de algunas personas relevantes o personajes típicos de su comunidad.",
    "levels": [
      "No Aplica",
      "Describe lo que llama su atención sobre el aporte de algunas personas relevantes o personajes típicos de su comunidad.",
      "Describe los aportes que han realizado algunas personas del país y el mundo en áreas como artes visuales, música, literatura, ciencia, deporte, política, entre otras.",
      "Reconoce personas que han destacado en el ámbito de las Artes, de la literatura y otras, explicadon su importancia.",
      "Identifica personas de origen extranjero que han aportado en diversos ámbitos de la cultura chilena",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
},
{
    "id": 37,
    "name": "Respeta normas de protección y seguridad determinadas en su establecimiento, por iniciativa propia.",
    "levels": [
      "No Aplica",
      "No Aplica",
      "Respeta normas de protección y seguridad determinadas en su establecimiento, por iniciativa propia.",
      "Aplican normas destinadas a su protección, tanto en la sala de clases como la escuela.",
      "Propone acciones para cuidar y proteger los espacios públicos que constituyen parte del entorno local y nacional.",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
},
{
    "id": 38,
    "name": "Identifica problemas ambientales de su entorno",
      "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Identifica problemas ambientales de su entorno",
      "No Aplica",
      "Identifica recursos presentes en objetos y bienes cotidianos, considerando recursos naturales renovables y no renovables",
      "No Aplica",
      "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
    "id": 39,
    "name": "Define acciones para los problemas ambientales de su entorno, asumiendolos a nivel individual y promoviendolo en la comunidad",
      "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Define acciones para los problemas ambientales de su entorno, asumiendolos a nivel individual y promoviendolo en la comunidad",
      "No Aplica",
      "Propone uso e importancia de cuidar los principales recursos naturales, en el marco de un desarrollo sostenble.",
      "No Aplica",
      "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
    "id": 40,
    "name": "Reconoce en el paisaje de su entorno cercano elementos naturales (bosques, ríos, etc,) y culturales (puentes, caminos, etc,)",
      "levels": [
      "Reconoce en el paisaje de su entorno cercano elementos naturales (bosques, ríos, etc,) y culturales (puentes, caminos, etc,)",
      "Señala (indica y nombra) lugares representativos de su entorno natural cercano (como bosques, volcanes, montañas, ríos, lagos, entre otros) al observarlos en maquetas, fotografías aéreas, dibujos o planos",
      "Señala (indica y nombra) lugares representativos de su localidad creados por el hombre (como iglesias, puentes, villas, entre otros) al observarlos en maquetas, fotografías aéreas, dibujos o planos, describiendo sus características.",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
    "id": 41,
    "name": "Nombra características de elementos naturales de su entorno cercano (como vegetación, agua, suelo, relieve, entre otros) al observarlas en dibujos, maquetas, fotografías aéreas o planos.",
      "levels": [
      "No Aplica",
      "Nombra características de elementos naturales de su entorno cercano (como vegetación, agua, suelo, relieve, entre otros) al observarlas en dibujos, maquetas, fotografías aéreas o planos.",
      "Señala (indica y nombra) lugares del entorno natural de diferentes zonas del país (como bosques, volcanes, montañas, ríos, lagos, entre otros) al observarlos en maquetas, fotografías aéreas, dibujos o planos, describiendo sus características.",
      "Nombra algunas características naturales de distintos paisajes utilizando correctamente vocabulario geográfico.",
      "Aplican vocabulario geográfico básico para describir los paisajes de Chile.",
      "Caracterizan el entorno geográfico de las civilizaciones estudiadas utilizando vocabulario geográfico adecuado (ej: continente, valle, montaña, océano, río, archipiélago, mares, ciudad, etc.)",
      "Describe distintos paisajes del continente americano, consoderando climas, ríos, población, idiomas, países, etc. utilizando vocabulario geográfico adecuado.",
      "No Aplica",
      "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
    "id": 42,
    "name": "Utiliza el nombre y la magnitud aproximada de distintas unidades temporales: día, semana, mes, año, estación.",
      "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Utiliza el nombre y la magnitud aproximada de distintas unidades temporales: día, semana, mes, año, estación.",
      "No Aplica",
      "Ubica en mapas las principales zonas climáticas del mundo, asociando las formas de vivir que han elaborado los habitantes.",
      "No Aplica",
      "No Aplica",
      "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
    "id": 43,
    "name": "Explica la utilidad que tienen diferentes formas de representación geográfica (como maquetas, fotografías aéreas, dibujos y planos), determinando cuál es más útil en determinados momentos.",
      "levels": [
      "No Aplica",
      "No Aplica",
      "Explica la utilidad que tienen diferentes formas de representación geográfica (como maquetas, fotografías aéreas, dibujos y planos), determinando cuál es más útil en determinados momentos.",
      "Crea planos simples que representen la sala de clases mediante simbología no convencional.",
      "Elabora planos simples de su entorno (por ejemplo, sala de clases, patio, pieza), utilizando simbología no convencional (pictórica), cuidando que se ajuste a la realidad observada.",
      "Ubica personas, lugares y elemenos en una cuadrícula, utilizando líneas de referencia y puntos cardinales.",
      "Ubica lugares en un mapa utilizando coordinadas geográficas como referencia (paralelos y meridianos)",
      "No Aplica",
      "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
    "id": 44,
    "name": "Distingue en fotografías o imágenes de paisajes característicos de su región elementos hechos por el ser humano.",
      "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Distingue en fotografías o imágenes de paisajes característicos de su región elementos hechos por el ser humano.",
      "No Aplica",
      "No Aplica",
      "Compara mediante la observación de imágenes, mapas y otras fuentes, paisajes de su región con paisajes de América",
      "No Aplica",
      "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
    "id": 45,
    "name": "Distingue diferentes formas de adaptación y transformación de la población a su ambiente natural.",
      "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Distingue diferentes formas de adaptación y transformación de la población a su ambiente natural.",
      "No Aplica",
      "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
    "id": 46,
    "name": "Describe con sus propias palabras el entorno local, utilizando categorías de ubicación relativa (derecha, izquierda, delante, detrás, entre otros).",
      "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Describe con sus propias palabras el entorno local, utilizando categorías de ubicación relativa (derecha, izquierda, delante, detrás, entre otros).",
      "Utiliza los puntos cardinales para ubicar diferentes lugares.",
      "Ubica diferenciando hemisferios, círculo del Ecuador, trópicos, polos, continentes y océanos del planeta y mapas y globos terráqueos.",
      "No Aplica",
      "No Aplica",
      "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
    "id": 47,
    "name": "Distingue entre trabajos remunerados y no remunerados de su localidad, respetando todo tipo de labor.",
      "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Distingue entre trabajos remunerados y no remunerados de su localidad, respetando todo tipo de labor.",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
    "id": 48,
    "name": "Reflexiona sobre los recursos naturales renovables y no renovables, reconociendo su carácter limitado y necesidad de cuidarlos.",
      "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Reflexiona sobre los recursos naturales renovables y no renovables, reconociendo su carácter limitado y necesidad de cuidarlos.",
      "No Aplica",
      "No Aplica"
      ],
      "selectedLevelIndex": null
    },
    {
    "id": 49,
    "name": "Reconoce los principales recursos naturales de América, considerando su distribución geogáfica y su uso.",
      "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Reconoce los principales recursos naturales de América, considerando su distribución geogáfica y su uso.",
      "No Aplica",
      "No Aplica"
      ],
      "selectedLevelIndex": null
    },
  {
    "id": 50,
    "name": "Distingue algunos actores de la organización política y democrática de Chile , como presidente, ministros, senadores, diputados y alcades",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Distingue algunos actores de la organización política y democrática de Chile , como presidente, ministros, senadores, diputados y alcades",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 51,
    "name": "Describe cómo la organización política es ejercida, nombrada o elegida.",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Describe cómo la organización política es ejercida, nombrada o elegida.",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 52,
    "name": "Identifica problemas que se resuelven mediante las distintas actividades laborales (municipalidad, hospital, bomberos, etc.)",
    "levels": [
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "Identifica problemas que se resuelven mediante las distintas actividades laborales (municipalidad, hospital, bomberos, etc.)",
      "Identifica la labor que cumplen en beneficio de la comunidad como los medios de transporte y de comunicación, y algunas instituciones encargadas de proteger nuestro patrimonio cultural y natural.",
      "Investiga resultados de algunas instituciones públicas o privadas, identificando el servicio que prestan en la comunidad y los diferentes trabajos que realizan las personas que se desempeñan en ellas.",
      "No Aplica",
      "No Aplica",
      "No Aplica"
    ],
    "selectedLevelIndex": null
  }
];

// Combinar ambas subdimensiones para compatibilidad
const allNatureSkills: Skill[] = [...encuentroNaturalezaSkills, ...sociedadSkills];

// Definición de la dimensión con datos hardcodeados y subdimensiones
export const encuentroNaturalezaDimension: Dimension = {
  id: 7,
  name: "Encuentro con la Naturaleza y Sociedad",
  skills: allNatureSkills, // Para compatibilidad
  subDimensions: [
    {
      id: 1,
      name: "Habilidades científicas",
      skills: encuentroNaturalezaSkills
    },
    {
      id: 2,
      name: "Habilidades históricas y geográficas",
      skills: sociedadSkills
    }
  ]
};

const LEVEL_COLORS = {
  expected: 'bg-yellow-50 border-2 border-yellow-400',    // Color amarillo claro para nivel esperado
  previous: 'bg-gray-50 border-2 border-gray-400',        // Color gris claro para nivel del año anterior
  selected: 'bg-blue-500 text-white shadow-md',           // Color azul para nivel seleccionado
  default: 'bg-white hover:bg-blue-50 border border-gray-200', // Color por defecto
  notApplicable: 'bg-gray-50 text-gray-400 italic',      // Color para "No aplica" no seleccionado
  notApplicableSelected: 'bg-blue-500 text-white shadow-md italic', // Color para "No aplica" seleccionado
  previouslySelected: 'bg-blue-50 border border-blue-300', // Color para nivel previamente seleccionado
};

const EncuentroNaturaleza: React.FC<{
  students: Student[];
  selectedStudentIds: (number | string)[];
  handleLevelChange: (studentId: number | string, dimensionId: number, skillId: number, levelIndex: number) => void;
  handleNotEvaluatedChange?: (studentId: number | string, dimensionId: number, skillId: number, notEvaluated: boolean) => void;
  viewMode: 'student' | 'skill';
  handleViewModeChange: (mode: 'student' | 'skill') => void;
  showInstructions: boolean;
  selectedCourse: string;
  setSelectedCourse: (course: string) => void;
  courses: string[];
  handleStudentSelect: (event: React.MouseEvent<HTMLButtonElement>, id: number | string) => void;
  getExpectedLevel: (age: number) => number;
  getPreviousYearLevel: (age: number) => number;
}> = ({
  students,
  selectedStudentIds,
  handleLevelChange,
  handleNotEvaluatedChange,
  viewMode,
  handleViewModeChange,
  showInstructions,
  selectedCourse,
  setSelectedCourse,
  courses,
  handleStudentSelect,
  getExpectedLevel,
  getPreviousYearLevel
}) => {
  console.log('EncuentroNaturaleza rendering', { students, selectedStudentIds, viewMode, selectedCourse });

  const [selectedSkillId, setSelectedSkillId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<"name" | "lastName" | "course">("lastName");
  const [showAllYears, setShowAllYears] = useState(true);
  const [skillsLoaded, setSkillsLoaded] = useState(true); // Ya están cargados desde el fallback
  
  // Estado para rastrear niveles previamente seleccionados
  const [previouslySelectedLevels, setPreviouslySelectedLevels] = useState<{[key: string]: number | null}>();
  
  // Estado para subdimensión activa (1 = Encuentro con la naturaleza, 2 = Sociedad)
  const [activeSubDimension, setActiveSubDimension] = useState<number>(1);

  // Función para obtener las habilidades de la subdimensión activa
  const getCurrentSubDimensionSkills = useCallback((): Skill[] => {
    const subDimension = encuentroNaturalezaDimension.subDimensions?.find(sub => sub.id === activeSubDimension);
    return subDimension ? subDimension.skills : encuentroNaturalezaSkills;
  }, [activeSubDimension]);

  // Habilidades de la subdimensión activa
  const currentSkills = useMemo(() => getCurrentSubDimensionSkills(), [getCurrentSubDimensionSkills]);

  // Funciones de utilidad para colores
  const getCompletionColor = (percentage: number, isSelected: boolean = false) => {
    if (isSelected) {
      if (percentage >= 75) return 'bg-green-600 text-white border-green-600';
      if (percentage >= 50) return 'bg-blue-600 text-white border-blue-600';
      if (percentage >= 25) return 'bg-yellow-600 text-white border-yellow-600';
      return 'bg-red-600 text-white border-red-600';
    }
    if (percentage >= 75) return 'bg-green-100 text-green-800 border-green-300 hover:bg-green-200';
    if (percentage >= 50) return 'bg-blue-100 text-blue-800 border-blue-300 hover:bg-blue-200';
    if (percentage >= 25) return 'bg-yellow-100 text-yellow-800 border-yellow-300 hover:bg-yellow-200';
    return 'bg-red-100 text-red-800 border-red-300 hover:bg-red-200';
  };

  const getPercentageTextColor = (percentage: number, isSelected: boolean = false) => {
    if (isSelected) return 'text-white';
    if (percentage >= 75) return 'text-green-700';
    if (percentage >= 50) return 'text-blue-700';
    if (percentage >= 25) return 'text-yellow-700';
    return 'text-red-700';
  };

  // Función para extraer el primer apellido
  // Formato esperado: "Apellido Paterno; Apellido Materno; Nombre"
  const getFirstLastName = (fullName: string): string => {
    const nameParts = fullName.trim().split(' ');
    // El primer apellido (apellido paterno) es la primera parte del nombre
    if (nameParts.length >= 1) {
      return nameParts[0].toLowerCase();
    }
    // Si está vacío, retornamos la cadena vacía
    return '';
  };

  useEffect(() => {
    if (viewMode === 'student' && selectedStudentIds.length === 0 && students.length > 0) {
      // Usar el primer estudiante disponible del curso actual, no hardcodear
      const firstStudent = students[0];
      if (firstStudent) {
        handleStudentSelect(new MouseEvent('click') as any, firstStudent.id as any);
      }
    } else if (viewMode === 'skill' && !selectedSkillId) {
      const firstSkill = getCurrentSubDimensionSkills()[0];
      if (firstSkill) {
        setSelectedSkillId(firstSkill.id);
      }
    }
  }, [viewMode, students, selectedStudentIds, handleStudentSelect, selectedSkillId, getCurrentSubDimensionSkills]);

  // useEffect para actualizar la habilidad seleccionada al cambiar de subdimensión
  useEffect(() => {
    if (viewMode === 'skill') {
      const currentSubDimensionSkills = getCurrentSubDimensionSkills();
      const firstSkill = currentSubDimensionSkills[0];
      if (firstSkill) {
        // Verificar si la habilidad actual pertenece a la subdimensión activa
        const currentSkillBelongsToSubDimension = selectedSkillId && currentSubDimensionSkills.some(skill => skill.id === selectedSkillId);
        
        // Si no hay habilidad seleccionada o la actual no pertenece a la subdimensión activa, seleccionar la primera
        if (!selectedSkillId || !currentSkillBelongsToSubDimension) {
          setSelectedSkillId(firstSkill.id);
        }
      }
    }
  }, [activeSubDimension, viewMode, selectedSkillId, getCurrentSubDimensionSkills]);

  // useEffect para inicializar los niveles previamente seleccionados desde la base de datos
  useEffect(() => {
    if (skillsLoaded && students.length > 0) {
      // Inicializar los niveles previamente guardados en la base de datos
      const initialLevels: {[key: string]: number} = {};
      
      students.forEach(student => {
        student.skillLevels.forEach(skillLevel => {
          if (skillLevel.dimensionId === encuentroNaturalezaDimension.id && skillLevel.level !== null) {
            initialLevels[`${student.id}-${skillLevel.skillId}`] = skillLevel.level;
          }
        });
      });
      
      // Solo actualizar si hay datos que inicializar
      if (Object.keys(initialLevels).length > 0) {
        setPreviouslySelectedLevels(initialLevels);
      }
    }
  }, [students, skillsLoaded]);

  const getSkillLevel = (student: Student, dimensionId: number, skillId: number): number | null => {
    const skillLevel = student.skillLevels.find(
      sl => sl.dimensionId === dimensionId && sl.skillId === skillId
    );
    return skillLevel?.level ?? null;
  };

  // 🚫 Función para verificar si una habilidad está marcada como "No evaluada"
  const isSkillNotEvaluated = (student: Student, dimensionId: number, skillId: number): boolean => {
    const skillLevel = student.skillLevels.find(
      sl => sl.dimensionId === dimensionId && sl.skillId === skillId
    );
    return skillLevel?.notEvaluated === true;
  };

  // 🎯 NUEVO: Helper compartido para obtener el nivel del año anterior REAL
  const getHistoricalLevel = useCallback((student: Student, skillId: number, currentAge: number) => {
    if (student.previousYearLevels && student.previousYearLevels.length > 0) {
      const historical = student.previousYearLevels.find(
        pl => pl.dimensionId === encuentroNaturalezaDimension.id && pl.skillId === skillId
      );
      if (historical !== undefined && historical.level !== null) {
        return historical.level;
      }
    }
    return getPreviousYearLevel(currentAge);
  }, []);

  // Función para calcular el progreso de la subdimensión activa
  const calculateSubDimensionProgress = useCallback((student: Student): number => {
    const currentSkills = getCurrentSubDimensionSkills();
    const totalSkills = currentSkills.length;
    const completedSkills = currentSkills.filter(skill => 
      getSkillLevel(student, encuentroNaturalezaDimension.id, skill.id) !== null
    ).length;
    return totalSkills > 0 ? (completedSkills / totalSkills) * 100 : 0;
  }, [getCurrentSubDimensionSkills]);

  const SkillsChart = useCallback(({ student }: { student: Student }) => {
    const birthDate = new Date(student.birthDate.split('-').reverse().join('-'));
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear() - 
      (today.getMonth() < birthDate.getMonth() || 
      (today.getMonth() === birthDate.getMonth() && today.getDate() < birthDate.getDate()) ? 1 : 0);

  const data = currentSkills.map(skill => ({
      name: skill.name,
      previousYearLevel: calculateLastYearPercentage(age),
      percentageAchieved: getSkillLevel(student, encuentroNaturalezaDimension.id, skill.id) === null 
        ? 0 
        : (getSkillLevel(student, encuentroNaturalezaDimension.id, skill.id)! + 1) * 20,
      expectedLevel: calculateExpectedLevel(age),
    }));

    const maxSkillNameLength = Math.max(...data.map(d => d.name.length));
    const yAxisWidth = Math.min(200, maxSkillNameLength * 8);
    const barSize = 40;
    const minChartHeight = 200;
    const heightPerItem = 60;
    const chartHeight = Math.max(minChartHeight, data.length * heightPerItem);

    const wrapText = (text: string, maxWidth: number) => {
      const words = text.split(' ');
      const lines = [];
      let currentLine = words[0];

      for (let i = 1; i < words.length; i++) {
        const word = words[i];
        const width = getTextWidth(currentLine + " " + word);
        if (width < maxWidth) {
          currentLine += " " + word;
        } else {
          lines.push(currentLine);
          currentLine = word;
        }
      }
      lines.push(currentLine);
      return lines;
    };

    const getTextWidth = (text: string) => {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (context) {
        context.font = '14px Arial';
        return context.measureText(text).width;
      }
      return 0;
    };

    return (
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <h3 className="text-xl font-bold mb-4">Gráfico de {encuentroNaturalezaDimension.name}</h3>
        <div style={{ width: '100%', height: `${chartHeight}px` }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              layout="vertical"
              data={data}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <XAxis 
                type="number" 
                domain={[0, 110]}
                ticks={[20, 60, 100]}
                tickFormatter={(value) => {
                  const ageMap: { [key: number]: string } = {
                    20: 'Hacia los 4 años',
                    60: 'Hacia los 6 años',
                    100: 'Hacia los 8 años'
                  };
                  return ageMap[value] || '';
                }}
                tick={{ fontSize: 14, fontWeight: 'bold' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis 
                dataKey="name" 
                type="category" 
                width={yAxisWidth}
                tick={({ x, y, payload }) => {
                  const lines = wrapText(payload.value, yAxisWidth - 20);
                  return (
                    <g transform={`translate(${x},${y})`}>
                      {lines.map((line, index) => (
                        <text
                          key={`item-${line}-${index}`}
                          x={-10}
                          y={index * 16}
                          dy={-8 * (lines.length - 1)}
                          textAnchor="end"
                          fill="#333"
                          fontSize={14}
                          fontWeight="bold"
                        >
                          {line}
                        </text>
                      ))}
                    </g>
                  );
                }}
                axisLine={false}
                tickLine={false}
              />
              <ZAxis range={[200, 200]} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar 
                dataKey="percentageAchieved" 
                fill={COLORS.achieved} 
                name="Nivel habilidad logrado" 
                barSize={barSize}
              />
              <Bar
                dataKey="expectedLevel"
                fill={COLORS.expected}
                name="Nivel esperado"
                barSize={4}
                shape={<CustomBar />}
              />
              <Scatter 
                dataKey="previousYearLevel" 
                fill={COLORS.previousYear} 
                name="Nivel año anterior" 
                shape={<CustomVerticalRectangle />}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }, [currentSkills]);

  const DimensionSummary = useCallback(({ student, dimensionId }: DimensionSummaryProps) => {
    const birthDate = new Date(student.birthDate.split('-').reverse().join('-'));
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear() - 
      (today.getMonth() < birthDate.getMonth() || 
      (today.getMonth() === birthDate.getMonth() && today.getDate() < birthDate.getDate()) ? 1 : 0);

    const percentageAchieved = currentSkills.reduce((sum, skill) => {
      const level = getSkillLevel(student, dimensionId, skill.id);
      // ✅ CORRECCIÓN: Añadir año base para alinear con edad real seleccionada
      return sum + (level === null ? 0 : (level + 1) * 20 + 20);
    }, 0) / currentSkills.length;

    const data = [{
      name: encuentroNaturalezaDimension.name,
      previousYearLevel: calculateLastYearPercentage(age),
      percentageAchieved,
      expectedLevel: calculateExpectedLevel(age),
    }];

    return (
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <h3 className="text-xl font-bold mb-4">Resumen de {encuentroNaturalezaDimension.name}</h3>
        <ResponsiveContainer width="100%" height={200}>
          <ComposedChart
            layout="vertical"
            data={data}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          >
            <XAxis 
              type="number" 
              domain={[0, 110]}
              ticks={[20, 60, 100]}
              tickFormatter={(value) => {
                const ageMap: { [key: number]: string } = {
                  20: 'Hacia los 4 años',
                  60: 'Hacia los 6 años',
                  100: 'Hacia los 8 años'
                };
                return ageMap[value] || '';
              }}
              tick={{ fontSize: 14, fontWeight: 'bold' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis 
              dataKey="name" 
              type="category" 
              width={150}
              tick={{ fontSize: 14, fontWeight: 'bold' }}
              axisLine={false}
              tickLine={false}
            />
            <ZAxis range={[200, 200]} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar 
              dataKey="percentageAchieved" 
              fill={COLORS.achieved} 
              name="Nivel habilidad logrado" 
              barSize={40}
            />
            <Bar
              dataKey="expectedLevel"
              fill={COLORS.expected}
              name="Nivel esperado"
              barSize={4}
              shape={<CustomBar />}
            />
            <Scatter 
              dataKey="previousYearLevel" 
              fill={COLORS.previousYear} 
              name="Nivel año anterior" 
              shape={<CustomVerticalRectangle />}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    );
  }, [currentSkills]);

  const handleLocalLevelChange = useCallback((studentId: number | string, skillId: number, levelIndex: number | null) => {
    const normalizeId = (id: number | string) => String(id ?? '').trim().toUpperCase();

    const student = students.find(s => normalizeId(s.id) === normalizeId(studentId));
    if (student) {
      const currentLevel = getSkillLevel(student, encuentroNaturalezaDimension.id, skillId);

      if (currentLevel !== null && !previouslySelectedLevels?.[`${studentId}-${skillId}`]) {
        setPreviouslySelectedLevels(prev => ({
          ...prev,
          [`${studentId}-${skillId}`]: currentLevel
        }));
      }

      // Lógica de toggle: si el nivel ya está seleccionado, deseleccionarlo
      if (currentLevel === levelIndex) {
        handleLevelChange(studentId, encuentroNaturalezaDimension.id, skillId, null);
        return;
      }
    }

    // Si levelIndex es -1, convertir a null (para compatibilidad con el botón de reiniciar)
    const actualLevelIndex = levelIndex === -1 ? null : levelIndex;
    handleLevelChange(studentId, encuentroNaturalezaDimension.id, skillId, actualLevelIndex);
  }, [handleLevelChange, students, previouslySelectedLevels]);

  const selectColumnForAllStudents = (levelIndex: number, students: Student[], selectedSkillId: number, handleLevelChange: Function) => {
    // Verificar que la habilidad seleccionada pertenezca a la subdimensión activa
    const currentSubDimensionSkills = getCurrentSubDimensionSkills();
    const skillBelongsToCurrentSubDimension = currentSubDimensionSkills.some(skill => skill.id === selectedSkillId);

    if (!skillBelongsToCurrentSubDimension) {
      console.warn('La habilidad seleccionada no pertenece a la subdimensión activa');
      return;
    }

    students.forEach(student => {
      const currentLevel = student.skillLevels.find(
        sl => sl.dimensionId === encuentroNaturalezaDimension.id && sl.skillId === selectedSkillId
      )?.level;

      if (currentLevel === levelIndex) {
        handleLevelChange(student.id as any, encuentroNaturalezaDimension.id, selectedSkillId, null);
      } else {
        handleLevelChange(student.id as any, encuentroNaturalezaDimension.id, selectedSkillId, levelIndex);
      }
    });
  };

  const selectColumnForAllSkills = (levelIndex: number, studentId: number | string, handleLevelChange: (studentId: number | string, dimensionId: number, skillId: number, level: number | null) => void) => {
    // Usar solo las habilidades de la subdimensión activa
    const currentSubDimensionSkills = getCurrentSubDimensionSkills();
    currentSubDimensionSkills.forEach(skill => {
      const currentLevel = students.find(s => s.id === studentId)?.skillLevels.find(
        sl => sl.dimensionId === encuentroNaturalezaDimension.id && sl.skillId === skill.id
      )?.level;

      if (currentLevel === levelIndex) {
        handleLevelChange(studentId, encuentroNaturalezaDimension.id, skill.id, null);
      } else {
        handleLevelChange(studentId, encuentroNaturalezaDimension.id, skill.id, levelIndex);
      }
    });
  };

  const renderStudentView = useMemo(() => {
    return () => selectedStudentIds.map(studentId => {
      const normalizeId = (id: number | string) => String(id ?? '').trim().toUpperCase();
      const student = students.find(s => normalizeId(s.id) === normalizeId(studentId));
      if (!student) return null;

      const age = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });

      const expectedLevel = getExpectedLevel(age);

      const allYears = Array.from({length: 9}, (_, i) => `Hacia los ${i + 4} años`);
      
      const getVisibleYears = () => {
        if (showAllYears) {
          return allYears;
        }
        
        const studentAge = age;
        const minAge = Math.max(4, studentAge - 2);
        const maxAge = Math.min(12, studentAge + 2);
        
        return allYears.filter((_, index) => {
          const yearAge = index + 4;
          return yearAge >= minAge && yearAge <= maxAge;
        });
      };
      
      const visibleYears = getVisibleYears();
      const visibleIndices = visibleYears.map((year, index) => {
      const match = year.match(/\d+/);
      const yearNum = match ? parseInt(match[0]) : 4;
      const calculatedIndex = yearNum - 4;
      // Asegurar que nunca sea NaN y usar fallback con index
      return isNaN(calculatedIndex) ? index : calculatedIndex;
    });

      return (
        <div key={student.id} className="mb-8">
          <div className="overflow-x-auto">
            <div className="bg-white rounded-lg overflow-hidden shadow-lg my-5 border border-gray-200">
              <div className="bg-gray-50 p-3 border-b border-gray-200">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-700">Progreso de habilidades:</span>
                    <div className="w-32 h-2.5 bg-gray-200 rounded-full">
                      {(() => {
                        // ✅ CORREGIDO: Usar cálculo excluyendo "No aplica"
                        const progress = calculateFormProgress(
                          encuentroNaturalezaDimension.id,
                          student.course,
                          student.skillLevels
                        );
                        const progressPercent = progress.percentage;
                        
                        let barColor = "bg-red-500";
                        if (progressPercent >= 75) barColor = "bg-green-500";
                        else if (progressPercent >= 50) barColor = "bg-blue-500";
                        else if (progressPercent >= 25) barColor = "bg-yellow-500";
                        
                        return (
                          <div 
                            className={`h-2.5 rounded-full ${barColor}`} 
                            style={{ width: `${progressPercent}%` }}
                          ></div>
                        );
                      })()}
                    </div>
                    <span className="text-xs text-gray-500">
                      {(() => {
                        const progress = calculateFormProgress(
                          encuentroNaturalezaDimension.id,
                          student.course,
                          student.skillLevels
                        );
                        return `${progress.completedSkills} de ${progress.totalValidSkills} habilidades`;
                      })()}
                    </span>
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowAllYears(!showAllYears)}
                      className={`text-xs font-medium px-3 py-1 border rounded-md transition-colors ${
                        showAllYears 
                          ? 'bg-blue-500 text-white border-blue-500 hover:bg-blue-600' 
                          : 'bg-white text-blue-500 border-blue-500 hover:bg-blue-50'
                      }`}
                    >
                      {showAllYears ? 'Filtrar por edad (±2 años)' : 'Mostrar todos los años (4-12)'}
                    </button>
                    
                    <button
                      onClick={() => {
                        if (window.confirm("¿Seguro que desea reiniciar todos los niveles de habilidad para este estudiante en esta subdimensión?")) {
                          const currentSubDimensionSkills = getCurrentSubDimensionSkills();
                          currentSubDimensionSkills.forEach(skill => {
                            handleLocalLevelChange(student.id as any, skill.id, -1);
                          });
                        }
                      }}
                      className="text-xs text-red-500 hover:text-red-700 font-medium bg-white px-2 py-1 border border-red-200 rounded-md hover:bg-red-50 transition-colors"
                    >
                      Reiniciar
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-indigo-700 to-indigo-800 text-white sticky top-0 z-10">
                    <tr>
                      <th className="py-3 px-6 text-left text-xs font-medium uppercase tracking-wider w-1/4">Habilidad</th>
                      {visibleYears.map((ageGroup, index) => {
                        const actualIndex = visibleIndices[index];
                        return (
                          <th key={`item-${ageGroup}-${index}`} className="py-3 px-4 text-center">
                            <div className="flex flex-col items-center">
                              <span className="mb-1">{ageGroup}</span>
                              <button
                                onClick={() => {
                                  const currentSubDimensionSkills = getCurrentSubDimensionSkills();
                                  currentSubDimensionSkills.forEach(skill => {
                                    const currentLevel = getSkillLevel(student, encuentroNaturalezaDimension.id, skill.id);
                                    if (currentLevel === actualIndex) {
                                      handleLocalLevelChange(student.id as any, skill.id, null);
                                    } else {
                                      handleLocalLevelChange(student.id as any, skill.id, actualIndex);
                                    }
                                  });
                                }}
                                className="px-2 py-1 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded text-xs font-medium transition-all"
                              >
                                Seleccionar todo
                              </button>
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentSkills.map((skill: Skill, skillIndex: number) => {
                      const selectedLevel = getSkillLevel(student, encuentroNaturalezaDimension.id, skill.id);
                      const skillIsNotEvaluated = isSkillNotEvaluated(student, encuentroNaturalezaDimension.id, skill.id);
                      
                      const previousYearLevel = getHistoricalLevel(student, skill.id, age);
                      
                      return (
                        <tr key={skill.id} className={`${skillIndex % 2 === 0 ? "bg-gray-50 hover:bg-blue-50" : "hover:bg-blue-50"} ${skillIsNotEvaluated ? "opacity-60" : ""}`}>
                          <td className="py-3 px-6 text-sm font-medium text-gray-900">
                            <div className="flex items-start">
                              <span className="rounded-full bg-indigo-100 text-indigo-800 w-6 h-6 flex items-center justify-center mr-2 font-bold text-xs">
                                {skillIndex + 1}
                              </span>
                              <div className="flex-1">
                                {skill.name}
                                {selectedLevel !== null && !skillIsNotEvaluated && (
                                  <div className="mt-1 text-xs text-green-600 font-medium">
                                    Nivel seleccionado: {selectedLevel + 1}
                                  </div>
                                )}
                                {skillIsNotEvaluated && (
                                  <div className="mt-1 text-xs text-orange-600 font-medium">
                                    🚫 No evaluada
                                  </div>
                                )}
                                {/* Botón para marcar como No Evaluada */}
                                {handleNotEvaluatedChange && (
                                  <div className="mt-2">
                                    <button
                                      onClick={() => handleNotEvaluatedChange(student.id, encuentroNaturalezaDimension.id, skill.id, !skillIsNotEvaluated)}
                                      className={`text-xs px-2 py-1 rounded border transition-all duration-200 ${
                                        skillIsNotEvaluated
                                          ? "bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200"
                                          : "bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200"
                                      }`}
                                      title={skillIsNotEvaluated ? "Habilitar evaluación" : "Marcar como no evaluada (no afectará el cálculo de nivel de logro)"}
                                    >
                                      {skillIsNotEvaluated ? "✓ Habilitar evaluación" : "🚫 No evaluada"}
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          {visibleIndices.map((levelIndex: number, mapIndex: number) => {
                          const level = skill.levels[levelIndex];
                          if (!level) return null;
                          
                          const isNotApplicable = level.toLowerCase().includes('no aplica');
                          const previouslySelected = previouslySelectedLevels && previouslySelectedLevels[`${student.id}-${skill.id}`] === levelIndex;
                          const isExpected = levelIndex === expectedLevel;
                          
                          let buttonClass = 'p-3 rounded text-sm border transition-all duration-200 min-h-[80px] w-full flex items-center justify-center ';
                          
                          const previousYearLevel = getHistoricalLevel(student, skill.id, age);
                          const isPrevious = levelIndex === previousYearLevel;

                            // 1. Fondo según selección o estado base
                            if (isNotApplicable) {
                              if (selectedLevel === levelIndex) {
                                buttonClass += LEVEL_COLORS.notApplicableSelected;
                              } else {
                                buttonClass += LEVEL_COLORS.notApplicable;
                              }
                            } else if (selectedLevel === levelIndex) {
                              buttonClass += LEVEL_COLORS.selected;
                            } else if (previouslySelected) {
                              buttonClass += LEVEL_COLORS.previouslySelected;
                            } else {
                              buttonClass += LEVEL_COLORS.default;
                            }

                            // 2. Bordes e indicadores adicionales (pueden coexistir con la selección)
                            let extraStyles: React.CSSProperties = {
                              boxSizing: 'border-box'
                            };

                            if (isPrevious) {
                              // Aplicar borde gris grueso para nivel año anterior
                              extraStyles.border = '4px solid #9CA3AF'; // Gris-400
                              if (selectedLevel !== levelIndex) {
                                extraStyles.backgroundColor = '#F9FAFB'; // Gris-50 si no está seleccionado
                              }
                            }

                            if (isExpected) {
                              // Si es esperado, aplicar borde amarillo
                              if (!isPrevious) {
                                extraStyles.border = '4px solid #FACC15'; // Amarillo-400
                                if (selectedLevel !== levelIndex) {
                                  extraStyles.backgroundColor = '#FEFCE8'; // Amarillo-50
                                }
                              }
                            }

                            return (
                              <td key={`student-${student.id}-skill-${skill.id}-level-${levelIndex}-map-${mapIndex}`} className="py-2 px-2 text-center">
                                <button
                                  onClick={() => handleLocalLevelChange(student.id as any, skill.id, levelIndex)}
                                  className={buttonClass}
                                  style={extraStyles}
                                  title={isNotApplicable ? "No aplica para este nivel" : `Nivel ${levelIndex + 1}: ${level}${isPrevious ? ' (Nivel año anterior)' : ''}${isExpected ? ' (Nivel esperado)' : ''}`}
                                >
                                  <span className="text-center leading-tight">{level}</span>
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {/* Controles de navegación rápida */}
              <div className="bg-gray-50 p-3 border-t border-gray-200 flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  <strong>Tip:</strong> Seleccione un nivel por cada habilidad.
                </div>
                <div className="flex gap-2">
                  <button 
                    className="px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium bg-white hover:bg-gray-50"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  >
                    Volver arriba
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    });
  }, [selectedStudentIds, students, getExpectedLevel, getPreviousYearLevel, showAllYears, currentSkills, getCurrentSubDimensionSkills, handleLocalLevelChange, previouslySelectedLevels]);

  // Estudiantes filtrados y ordenados
  const filteredStudents = useMemo(() => {
    return students
      .filter(student => selectedCourse ? student.course === selectedCourse : true)
      .filter(student => 
        searchTerm === '' || 
        student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.course.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => {
        if (sortBy === 'name') return a.name.localeCompare(b.name);
        if (sortBy === 'lastName') {
          const lastNameA = getFirstLastName(a.name);
          const lastNameB = getFirstLastName(b.name);
          return lastNameA.localeCompare(lastNameB);
        }
        if (sortBy === 'course') return a.course.localeCompare(b.course);
        return 0;
      });
  }, [students, selectedCourse, searchTerm, sortBy, getFirstLastName]);

  const renderSkillView = useMemo(() => {
    return () => {
      if (!selectedSkillId) return null;
      
      const skill = currentSkills.find(s => s.id === selectedSkillId);
      if (!skill) return null;

      const allYears = Array.from({length: 9}, (_, i) => `Hacia los ${i + 4} años`);
      
      const getVisibleYears = () => {
        if (showAllYears) {
          return allYears;
        }
        
        const averageAge = filteredStudents.length > 0 ? 
          filteredStudents.reduce((sum, student) => {
            const a = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });
            return sum + a;
          }, 0) / filteredStudents.length : 6;
        
        const minAge = Math.max(4, Math.floor(averageAge) - 2);
        const maxAge = Math.min(12, Math.floor(averageAge) + 2);
        
        return allYears.filter((_, index) => {
          const yearAge = index + 4;
          return yearAge >= minAge && yearAge <= maxAge;
        });
      };
      
      const visibleYears = getVisibleYears();
      const visibleIndices = visibleYears.map((year, index) => {
      const match = year.match(/\d+/);
      const yearNum = match ? parseInt(match[0]) : 4;
      const calculatedIndex = yearNum - 4;
      // Asegurar que nunca sea NaN y usar fallback con index
      return isNaN(calculatedIndex) ? index : calculatedIndex;
    });

      // Estado masivo de "No evaluada" para la habilidad seleccionada
      const allStudentsNotEvaluated =
        filteredStudents.length > 0 &&
        filteredStudents.every(student =>
          isSkillNotEvaluated(student, encuentroNaturalezaDimension.id, skill.id)
        );

      const toggleNotEvaluatedForAll = () => {
        if (!handleNotEvaluatedChange) return;
        const nextState = !allStudentsNotEvaluated;
        filteredStudents.forEach(student => {
          handleNotEvaluatedChange(
            student.id,
            encuentroNaturalezaDimension.id,
            selectedSkillId,
            nextState
          );
        });
      };

      return (
        <div className="mb-8">
          <div className="overflow-x-auto">
            <div className="bg-white rounded-lg overflow-hidden shadow-lg my-5 border border-gray-200">
              <div className="bg-gray-50 p-3 border-b border-gray-200">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-700">Progreso de la habilidad:</span>
                    <div className="w-32 h-2.5 bg-gray-200 rounded-full">
                      {(() => {
                        const totalStudents = filteredStudents.length;
                        const completedStudents = filteredStudents.filter(student => 
                          getSkillLevel(student, encuentroNaturalezaDimension.id, skill.id) !== null
                        ).length;
                        const progressPercent = totalStudents > 0 ? (completedStudents / totalStudents) * 100 : 0;
                        
                        let barColor = "bg-red-500";
                        if (progressPercent >= 75) barColor = "bg-green-500";
                        else if (progressPercent >= 50) barColor = "bg-blue-500";
                        else if (progressPercent >= 25) barColor = "bg-yellow-500";
                        
                        return (
                          <div 
                            className={`h-2.5 rounded-full ${barColor}`} 
                            style={{ width: `${progressPercent}%` }}
                          ></div>
                        );
                      })()}
                    </div>
                    <span className="text-xs text-gray-500">
                      {filteredStudents.filter(student => 
                        getSkillLevel(student, encuentroNaturalezaDimension.id, skill.id) !== null
                      ).length} de {filteredStudents.length} estudiantes
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">{skill.name}</span>
                    <button
                      onClick={() => setShowAllYears(!showAllYears)}
                      className={`text-xs font-medium px-3 py-1 border rounded-md transition-colors ${
                        showAllYears 
                          ? 'bg-blue-500 text-white border-blue-500 hover:bg-blue-600' 
                          : 'bg-white text-blue-500 border-blue-500 hover:bg-blue-50'
                      }`}
                    >
                      {showAllYears ? 'Filtrar por edad (±2 años)' : 'Mostrar todos los años (4-12)'}
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-indigo-700 to-indigo-800 text-white sticky top-0 z-10">
                    <tr>
                      <th className="py-3 px-6 text-left text-xs font-medium uppercase tracking-wider w-1/4">
                        <div className="flex flex-col items-start gap-1">
                          <span>Estudiante</span>
                          {handleNotEvaluatedChange && (
                            <button
                              onClick={toggleNotEvaluatedForAll}
                              className={`px-3 py-1.5 rounded text-[11px] font-semibold transition-all border ${
                                allStudentsNotEvaluated
                                  ? "bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200"
                                  : "bg-white text-indigo-900 border-white/50 hover:bg-indigo-50"
                              }`}
                              title="Marcar esta habilidad como no evaluada para todos los estudiantes filtrados"
                            >
                              {allStudentsNotEvaluated
                                ? "✓ Habilitar evaluación"
                                : "🚫 No evaluar esta habilidad"}
                            </button>
                          )}
                        </div>
                      </th>
                      {visibleYears.map((ageGroup, index) => {
                        const actualIndex = visibleIndices[index];
                        return (
                          <th key={`item-${ageGroup}-${index}`} className="py-3 px-4 text-center">
                            <div className="flex flex-col items-center">
                              <span className="mb-1">{ageGroup}</span>
                              <button
                                onClick={() => {
                                  filteredStudents.forEach(student => {
                                    const currentLevel = getSkillLevel(student, encuentroNaturalezaDimension.id, selectedSkillId);
                                    if (currentLevel === actualIndex) {
                                      handleLocalLevelChange(student.id as any, selectedSkillId, null);
                                    } else {
                                      handleLocalLevelChange(student.id as any, selectedSkillId, actualIndex);
                                    }
                                  });
                                }}
                                className="px-2 py-1 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded text-xs font-medium transition-all"
                              >
                                Seleccionar todo
                              </button>
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredStudents.map((student, studentIndex) => {
                      const selectedLevel = getSkillLevel(student, encuentroNaturalezaDimension.id, selectedSkillId);
                      const skillIsNotEvaluated = isSkillNotEvaluated(student, encuentroNaturalezaDimension.id, selectedSkillId);
                      
                      const age = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });
                      
                      return (
                        <tr key={student.id} className={`${studentIndex % 2 === 0 ? "bg-gray-50 hover:bg-blue-50" : "hover:bg-blue-50"} ${skillIsNotEvaluated ? "opacity-60" : ""}`}>
                          <td className="py-3 px-6 text-sm font-medium text-gray-900">
                            <div className="flex items-start">
                              <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center mr-2">
                                <span className="text-indigo-800 font-bold text-xs">{student.name.split(' ')[0][0]}{student.name.split(' ')[1]?.[0]}</span>
                              </div>
                              <div className="ml-1 flex-1">
                                <div className="font-medium">{student.name}</div>
                                <div className="text-xs text-gray-500">{student.course}</div>
                                {selectedLevel !== null && !skillIsNotEvaluated && (
                                  <div className="mt-1 text-xs text-green-600 font-medium">
                                    Nivel seleccionado: {selectedLevel + 1}
                                  </div>
                                )}
                                {skillIsNotEvaluated && (
                                  <div className="mt-1 text-xs text-orange-600 font-medium">
                                    🚫 No evaluada
                                  </div>
                                )}
                                {/* Botón para marcar como No Evaluada */}
                                {handleNotEvaluatedChange && (
                                  <div className="mt-2">
                                    <button
                                      onClick={() => handleNotEvaluatedChange(student.id, encuentroNaturalezaDimension.id, selectedSkillId, !skillIsNotEvaluated)}
                                      className={`text-xs px-2 py-1 rounded border transition-all duration-200 ${
                                        skillIsNotEvaluated
                                          ? "bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200"
                                          : "bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200"
                                      }`}
                                      title={skillIsNotEvaluated ? "Habilitar evaluación" : "Marcar como no evaluada (no afectará el cálculo de nivel de logro)"}
                                    >
                                      {skillIsNotEvaluated ? "✓ Habilitar evaluación" : "🚫 No evaluada"}
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          {visibleIndices.map((levelIndex: number, mapIndex: number) => {
                            const level = skill.levels[levelIndex];
                            if (!level) return null;
                            
                            const isNotApplicable = level.toLowerCase().includes('no aplica');
                            const previouslySelected = previouslySelectedLevels && previouslySelectedLevels[`${student.id}-${skill.id}`] === levelIndex;
                            
                            let buttonClass = 'p-3 rounded text-sm border transition-all duration-200 min-h-[80px] w-full flex items-center justify-center ';
                            
                            if (isNotApplicable) {
                              if (selectedLevel === levelIndex) {
                                buttonClass += LEVEL_COLORS.notApplicableSelected;
                              } else {
                                buttonClass += LEVEL_COLORS.notApplicable;
                              }
                            } else if (selectedLevel === levelIndex) {
                              buttonClass += LEVEL_COLORS.selected;
                            } else if (previouslySelected) {
                              buttonClass += LEVEL_COLORS.previouslySelected;
                            } else if (levelIndex === getExpectedLevel(age)) {
                              buttonClass += LEVEL_COLORS.expected;
                            } else if (levelIndex === getPreviousYearLevel(age)) {
                              buttonClass += LEVEL_COLORS.previous;
                            } else {
                              buttonClass += LEVEL_COLORS.default;
                            }

                            return (
                              <td key={`student-${student.id}-skill-${skill.id}-level-${levelIndex}-map-${mapIndex}`} className="py-2 px-2 text-center">
                                <button
                                  onClick={() => handleLocalLevelChange(student.id as any, selectedSkillId, levelIndex)}
                                  className={buttonClass}
                                  title={isNotApplicable ? "No aplica para este nivel" : `Nivel ${levelIndex + 1}: ${level}`}
                                >
                                  <span className="text-center leading-tight">{level}</span>
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {/* Controles de navegación rápida */}
              <div className="bg-gray-50 p-3 border-t border-gray-200 flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  <strong>Tip:</strong> Seleccione un nivel por cada habilidad.
                </div>
                <div className="flex gap-2">
                  <button 
                    className="px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium bg-white hover:bg-gray-50"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  >
                    Volver arriba
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    };
  }, [selectedSkillId, filteredStudents, handleLocalLevelChange, showAllYears, getExpectedLevel, getPreviousYearLevel, currentSkills, previouslySelectedLevels]);

  const renderSelectionButtons = () => {
    if (viewMode === 'student') {
      return (
        <div className="mb-6 bg-white p-5 rounded-lg shadow-md border border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
            <h3 className="text-lg font-semibold whitespace-nowrap">Seleccionar Estudiante:</h3>
            
            <div className="flex flex-col md:flex-row justify-center items-center w-full gap-3">
              {/* Barra de búsqueda */}
              <div className="relative w-full md:w-96 mx-auto">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 20 20">
                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"/>
                  </svg>
                </div>
                <input 
                  type="text" 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full p-2.5 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500" 
                  placeholder="Buscar estudiante..." 
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                )}
              </div>
              
              {/* Selector de ordenamiento */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-600">Ordenar por:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as "name" | "lastName" | "course")}
                  className="p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="name">Nombre</option>
                  <option value="lastName">Apellido</option>
                  <option value="course">Curso</option>
                </select>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {filteredStudents.length === 0 ? (
              <div className="col-span-full text-center py-4 text-gray-500">
                No se encontraron estudiantes con "{searchTerm}"
              </div>
            ) : (
              filteredStudents.map((student) => {
                // ✅ CORREGIDO: Usar cálculo excluyendo "No aplica"
                const progress = calculateFormProgress(
                  encuentroNaturalezaDimension.id,
                  student.course,
                  student.skillLevels
                );
                const normalizeId = (id: number | string) => String(id ?? '').trim().toUpperCase();
                const percentage = progress.percentage;
                const isSelected = student.id && selectedStudentIds.some(sel => normalizeId(sel) === normalizeId(student.id));
                
                return (
                <button
                  key={student.id}
                  onClick={(event) => handleStudentSelect(event, student.id as any)}
                    className={`p-3 rounded-lg border-2 transition-all duration-200 text-sm font-medium ${
                      getCompletionColor(percentage, isSelected)
                    }`}
                  >
                    <div className="font-semibold" title={`${student.name} - ${student.course}`}>
                  {student.name}
                    </div>
                    <div className={`text-xs font-medium ${getPercentageTextColor(percentage, isSelected)}`}>
                      {percentage.toFixed(1)}% completado
                    </div>
                </button>
                );
              })
            )}
          </div>
          
          <div className="mt-4 pt-3 border-t border-gray-200">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center flex-wrap">
                <p className="text-sm font-medium text-gray-700 mr-2">Leyenda:</p>
                              <div className="flex items-center mr-2">
                <div className="w-4 h-4 bg-green-500 rounded mr-1.5"></div>
                <span className="text-xs text-gray-600">
                  <span className="font-medium">≥75% completado,</span>
                </span>
              </div>
              <div className="flex items-center mr-2">
                <div className="w-4 h-4 bg-blue-500 rounded mr-1.5"></div>
                <span className="text-xs text-gray-600">
                  <span className="font-medium">≥50% completado,</span>
                </span>
              </div>
              <div className="flex items-center mr-2">
                <div className="w-4 h-4 bg-yellow-500 rounded mr-1.5"></div>
                <span className="text-xs text-gray-600">
                  <span className="font-medium">≥25% completado,</span>
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-red-500 rounded mr-1.5"></div>
                <span className="text-xs text-gray-600">&lt;25% completado</span>
              </div>
              </div>
              <span className="text-xs text-gray-600 font-medium bg-blue-50 p-1 px-2 rounded border border-blue-200">
                Tip: Ctrl + clic para seleccionar múltiples estudiantes
              </span>
            </div>
          </div>
        </div>
      );
    }
    
      return (
      <div className="mb-6 bg-white p-5 rounded-lg shadow-md border border-gray-200">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
          <h3 className="text-lg font-semibold whitespace-nowrap">Seleccionar Habilidad:</h3>
          
          <div className="flex flex-col md:flex-row justify-center items-center w-full gap-3">
            {/* Barra de búsqueda */}
            <div className="relative w-full md:w-96 mx-auto">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 20 20">
                  <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"/>
                </svg>
              </div>
              <input 
                type="text" 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full p-2.5 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500" 
                placeholder="Buscar habilidad..." 
              />
              {searchTerm && (
                <button 
                  onClick={() => setSearchTerm("")}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              )}
            </div>
            
            {/* Selector de ordenamiento */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-600">Ordenar por:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as "name" | "lastName" | "course")}
                className="p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="name">Nombre</option>
                <option value="lastName">Número</option>
                <option value="course">% Completado</option>
              </select>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {currentSkills
            .filter(skill => 
              searchTerm === '' || 
              skill.name.toLowerCase().includes(searchTerm.toLowerCase())
            )
            .sort((a, b) => {
              if (sortBy === 'name') return a.name.localeCompare(b.name);
              if (sortBy === 'lastName') return a.id - b.id; // Por número
              if (sortBy === 'course') { // Por % completado
                const aCompleted = filteredStudents.filter(student => 
                  getSkillLevel(student, encuentroNaturalezaDimension.id, a.id) !== null
                ).length;
                const bCompleted = filteredStudents.filter(student => 
                  getSkillLevel(student, encuentroNaturalezaDimension.id, b.id) !== null
                ).length;
                const aPercentage = filteredStudents.length > 0 ? (aCompleted / filteredStudents.length) * 100 : 0;
                const bPercentage = filteredStudents.length > 0 ? (bCompleted / filteredStudents.length) * 100 : 0;
                return bPercentage - aPercentage;
              }
              return 0;
            })
            .map((skill) => {
              const completed = filteredStudents.filter(student => 
                getSkillLevel(student, encuentroNaturalezaDimension.id, skill.id) !== null
              ).length;
              const percentage = filteredStudents.length > 0 ? (completed / filteredStudents.length) * 100 : 0;
              
              return (
              <button
                key={skill.id}
                onClick={() => setSelectedSkillId(skill.id)}
                  className={`p-3 rounded-lg border-2 transition-all duration-200 text-sm font-medium ${
                    getCompletionColor(percentage, selectedSkillId === skill.id)
                  }`}
                >
                  <div className="font-semibold" title={skill.name}>
                {skill.name}
                  </div>
                  <div className={`text-xs font-medium ${getPercentageTextColor(percentage, selectedSkillId === skill.id)}`}>
                    {percentage.toFixed(1)}% completado
                  </div>
              </button>
              );
            })}
        </div>
        
        <div className="mt-4 pt-3 border-t border-gray-200">
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center flex-wrap">
              <p className="text-sm font-medium text-gray-700 mr-2">Leyenda:</p>
              <div className="flex items-center mr-2">
                <div className="w-4 h-4 bg-green-500 rounded mr-1.5"></div>
                <span className="text-xs text-gray-600">
                  <span className="font-medium">≥75% completado,</span>
                </span>
              </div>
              <div className="flex items-center mr-2">
                <div className="w-4 h-4 bg-blue-500 rounded mr-1.5"></div>
                <span className="text-xs text-gray-600">
                  <span className="font-medium">≥50% completado,</span>
                </span>
              </div>
              <div className="flex items-center mr-2">
                <div className="w-4 h-4 bg-yellow-500 rounded mr-1.5"></div>
                <span className="text-xs text-gray-600">
                  <span className="font-medium">≥25% completado,</span>
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-red-500 rounded mr-1.5"></div>
                <span className="text-xs text-gray-600">&lt;25% completado</span>
              </div>
            </div>
            <span className="text-xs text-gray-600 font-medium bg-blue-50 p-1 px-2 rounded border border-blue-200">
              Tip: Selecciona una habilidad para ver detalles
            </span>
          </div>
          </div>
        </div>
      );
  };

  const renderDataInputView = useMemo(() => {
    return () => (
      <div>
        {viewMode === 'student' ? renderStudentView() : renderSkillView()}
      </div>
    );
  }, [viewMode, renderStudentView, renderSkillView]);



  return (
    <div className={DIMENSION_STYLES.container}>
      <UnifiedHeader
        dimensionName={encuentroNaturalezaDimension.name}
        icon={FaTree}
        skillsCount={getCurrentSubDimensionSkills().length}
        selectedCourse={selectedCourse}
        courses={courses}
        onCourseChange={setSelectedCourse}
      />
      
      {/* Pestañas de Subdimensiones */}
      <div className="mt-4 mb-4">
        <div className="bg-gray-100 p-1 rounded-lg shadow-sm max-w-lg">
          <div className="grid grid-cols-2 gap-1">
            <button
              onClick={() => setActiveSubDimension(1)}
              className={`flex items-center justify-center py-3 px-4 rounded-md text-sm transition-all duration-200 ${
                activeSubDimension === 1
                  ? 'bg-indigo-600 text-white shadow-md' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FaTree className="mr-2 text-sm" />
              <span className="font-medium">Habilidades científicas</span>
              <span className="ml-2 px-2 py-1 text-xs bg-indigo-100 text-indigo-800 rounded-full">
                {encuentroNaturalezaSkills.length}
              </span>
            </button>
            
            <button
              onClick={() => setActiveSubDimension(2)}
              className={`flex items-center justify-center py-3 px-4 rounded-md text-sm transition-all duration-200 ${
                activeSubDimension === 2
                  ? 'bg-indigo-600 text-white shadow-md' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FaUser className="mr-2 text-sm" />
              <span className="font-medium">Habilidades históricas y geográficas</span>
              <span className="ml-2 px-2 py-1 text-xs bg-indigo-100 text-indigo-800 rounded-full">
                {sociedadSkills.length}
              </span>
            </button>
          </div>
        </div>
      </div>
        
      {/* Botones de modo de vista */}
        <div className="mt-3">
          <div className="bg-gray-100 p-1 rounded-lg shadow-sm max-w-md">
            <div className="grid grid-cols-2 gap-1">
              <button
                onClick={() => handleViewModeChange('student')}
                className={`flex items-center justify-center py-2 px-3.5 rounded-md text-sm transition-all duration-200 ${
                  viewMode === 'student' 
                    ? 'bg-indigo-600 text-white shadow-md' 
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <FaUser className="mr-1.5 text-sm" />
                <span className="font-medium">Por Estudiante</span>
            </button>
              
            <button
                onClick={() => handleViewModeChange('skill')}
                className={`flex items-center justify-center py-2 px-3.5 rounded-md text-sm transition-all duration-200 ${
                  viewMode === 'skill' 
                    ? 'bg-indigo-600 text-white shadow-md' 
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <FaList className="mr-1.5 text-sm" />
                <span className="font-medium">Por Habilidad</span>
            </button>
            </div>
          </div>
        </div>
      
      {renderSelectionButtons()}
      
      <div className="bg-white p-4 rounded-lg shadow-md">
        {renderDataInputView()}
        
        {/* Leyenda de colores para los niveles de las tablas */}
        {(viewMode === 'student' && selectedStudentIds.length > 0) || (viewMode === 'skill' && selectedSkillId !== null) ? (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
              <span className="mr-2">🎯</span>
              Leyenda de colores para niveles de desarrollo:
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <div className="flex items-center">
                <div className="w-6 h-6 bg-blue-500 rounded mr-2"></div>
                <span className="text-xs text-gray-700">Nivel seleccionado (evaluado)</span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-yellow-50 border-2 border-yellow-400 rounded mr-2"></div>
                <span className="text-xs text-gray-700">Nivel esperado (edad correspondiente)</span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-gray-50 border-2 border-gray-400 rounded mr-2"></div>
                <span className="text-xs text-gray-700">Nivel del año anterior</span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-white border border-gray-200 rounded mr-2"></div>
                <span className="text-xs text-gray-700">Sin seleccionar</span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-gray-50 rounded mr-2 flex items-center justify-center">
                  <span className="text-xs text-gray-400">N/A</span>
                </div>
                <span className="text-xs text-gray-700">No aplica para este nivel</span>
              </div>
            </div>
            
            {/* Footer con botón "Volver arriba" */}
            <div className="mt-4 pt-3 border-t border-gray-200 flex justify-center">
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="px-4 py-2 bg-indigo-50 text-indigo-700 text-sm font-medium rounded-lg hover:bg-indigo-100 transition-colors flex items-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 10l7-7m0 0l7 7m-7-7v18"></path>
                </svg>
                Volver arriba
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

// Funciones auxiliares para cálculos de porcentajes
// Usar funciones centralizadas

const calculateExpectedLevel = (age: number) => {
  return calculateExpectedPercentage(age);
};

const calculateLastYearPercentage = (age: number) => {
  return calculatePreviousYearPercentage(age);
};

export default EncuentroNaturaleza;